import { DatePicker } from "@/common/components/DatePicker";
import { ApiError } from "@/common/lib/api";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { useRecordCalving } from "../api/animalsApi";
import type { AnimalDetail } from "../types";

type Props = {
  accountId: string;
  animal: AnimalDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function RecordCalvingDialog({ accountId, animal, open, onOpenChange, onSuccess }: Props) {
  const mutation = useRecordCalving(accountId, animal.id);

  const [occurredAt, setOccurredAt] = useState<Date | undefined>(undefined);
  const [lactationNumber, setLactationNumber] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOccurredAt(new Date());
    setLactationNumber("");
    setError(null);
  }, [open]);

  const birthDate = new Date(animal.birthDate);
  const today = new Date();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!occurredAt) {
      setError("Selecciona la fecha del parto.");
      return;
    }
    const parsed = Number.parseInt(lactationNumber, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError("Ingresa un número de parto mayor a 0.");
      return;
    }

    try {
      await mutation.mutateAsync({
        occurredAt: occurredAt.toISOString(),
        lactationNumber: parsed,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError("Ocurrió un error inesperado.");
      }
    }
  }

  const pending = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar parto</DialogTitle>
          <DialogDescription>
            La vaca quedará lactando a partir de la fecha del parto.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="calving-date" className="font-medium text-[13px] text-app-ink-2">
              Fecha del parto
            </label>
            <DatePicker
              id="calving-date"
              aria-label="Fecha del parto"
              value={occurredAt}
              onChange={setOccurredAt}
              minDate={birthDate}
              maxDate={today}
              disabled={pending}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="lactation-number" className="font-medium text-[13px] text-app-ink-2">
              Número de parto
            </label>
            <input
              id="lactation-number"
              type="number"
              inputMode="numeric"
              step={1}
              value={lactationNumber}
              onChange={(e) => setLactationNumber(e.target.value)}
              disabled={pending}
              placeholder="Ej. 2"
              className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20"
            />
            <p className="text-[11px] text-app-ink-3">
              Este es el segundo parto en adelante. Para el primero usa "Registrar primer parto".
            </p>
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <button
                type="button"
                disabled={pending}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
              >
                Cancelar
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={pending}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
            >
              {pending ? "Registrando…" : "Registrar parto"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
